import { useSyncExternalStore } from "react";

/** The ePro logo at the top of every client page. */
export function BrandHeader() {
  return (
    <header className="mb-6">
      <img src="/logo.png" alt="ePro Associates" width="4000" height="1000" className="h-12 w-auto sm:h-14" />
    </header>
  );
}

/** A controller's bar: label, percentage, track. Hidden until started. */
export function ProgressBar({ progress, className = "mt-3", fill = "bg-emerald-500" }) {
  const state = useSyncExternalStore(progress.subscribe, progress.getSnapshot);
  if (!state.visible) return null;
  const value = Math.round(state.value);
  return (
    <div className={className}>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <span className="min-w-0 break-words text-xs font-medium text-slate-600">{state.label}</span>
        <span className="shrink-0 text-xs font-medium tabular-nums text-slate-400">{value}%</span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={value}
        aria-label={state.label}
      >
        <div className={`bar-fill h-full rounded-full ${state.failed ? "bg-red-500" : fill}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

const STATUS_TONE = {
  pending: "text-slate-400",
  ready: "text-slate-600",
  uploading: "text-amber-600",
  processing: "text-amber-600",
  success: "text-emerald-600",
  error: "text-red-600",
};
// Every status carries a glyph as well as a colour, so colour is never the
// only thing communicating state.
const STATUS_GLYPH = { pending: "○", ready: "●", uploading: "↻", processing: "↻", success: "✓", error: "!" };
const STATUS_LABEL = {
  pending: "Not uploaded ｜ 未上传",
  ready: "Ready ｜ 已选择",
  uploading: "Uploading… ｜ 上传中",
  processing: "Processing… ｜ 处理中",
  success: "Uploaded ｜ 已上传",
  error: "Failed ｜ 失败",
};

/** "✓ Uploaded ｜ 已上传" — a glyph plus words in the state's colour. */
export function StatusLine({ state = "pending", text, className = "" }) {
  return (
    <span className={`shrink-0 text-xs font-medium ${STATUS_TONE[state] || STATUS_TONE.pending} ${className}`} data-state={state}>
      {STATUS_GLYPH[state] || STATUS_GLYPH.pending} {text || STATUS_LABEL[state] || ""}
    </span>
  );
}

/**
 * A success line, or an error with a heading, the real message and a way to
 * try again. `message` empty renders nothing.
 */
export function Alert({ ok, heading, message, onRetry, className = "mt-6" }) {
  if (!message && !heading) return null;
  if (ok) {
    return (
      <div role="status" className={`${className} rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3.5 text-sm text-emerald-800`}>
        {message}
      </div>
    );
  }
  return (
    <div role="alert" className={`${className} rounded-xl border border-red-200 bg-red-50 px-4 py-3.5 text-sm text-red-800`}>
      {heading && <p className="font-semibold">{heading}</p>}
      {message && <p className="mt-1 break-words text-red-700">{message}</p>}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 cursor-pointer rounded-full border-0 bg-red-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-700"
        >
          Try again ｜ 重试
        </button>
      )}
    </div>
  );
}

/** "Submission received" and the like: a tick, a heading, a line. */
export function SuccessState({ title, text }) {
  return (
    <div className="py-10 text-center">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
        <svg className="h-7 w-7 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
      <p className="mt-2 text-sm text-slate-500">{text}</p>
    </div>
  );
}

/** The shimmering placeholder blocks shown while a page loads. */
export function Skeleton({ className }) {
  return <div className={`skeleton ${className}`} />;
}

/** Screen-reader announcements for status changes. */
export function Announcer({ message }) {
  return (
    <div className="sr-only" role="status" aria-live="polite">
      {message}
    </div>
  );
}

export function Spinner() {
  return (
    <span className="spin" aria-hidden="true">
      ↻
    </span>
  );
}
